import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

const faqItems = [
  {
    q: '港片影志是一个什么样的网站？',
    a: '港片影志是一个专注于香港电影影史研究的专业文化平台，系统梳理香港三级片的发展历程、分级制度演变、经典代表作品以及其对华语流行文化的深远影响。我们致力于为影迷和研究者提供权威、客观、有深度的影史参考资料。',
  },
  {
    q: '网站内容是否包含不当或不适宜的素材？',
    a: '本站所有内容均以影史研究和文化评论为出发点，不包含任何不当图片或视频素材。文章配图均为经过艺术处理的电影文化意象图，所有文字内容均围绕电影历史、艺术价值和文化影响展开学术性讨论。',
  },
  {
    q: '网站的内容来源是否可靠？',
    a: '我们的编辑团队由资深影迷和电影研究者组成，所有文章内容均参考公开的影史资料、学术研究和权威媒体报道，并经多人交叉校对后发布，力求做到客观准确。',
  },
  {
    q: '如何与编辑团队取得联系？',
    a: '您可以通过网站底部的联系邮箱与我们取得联系，也可以使用本页面的联系表单提交您的意见、建议或合作意向。我们通常会在2-3个工作日内回复。',
  },
  {
    q: '是否接受投稿或合作？',
    a: '我们欢迎电影研究者和资深影迷投稿。如果您对香港电影史有独到的研究和见解，欢迎通过联系邮箱将稿件或合作意向发送给我们，编辑团队会认真审阅每一封来信。',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formUrl = 'https://readdy.ai/api/form/d9tfm2t3pcjqs2fcsda0';
    fetch(formUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then(async (response) => {
        const responseText = await response.text();
        let parsed;
        try {
          parsed = JSON.parse(responseText);
        } catch {
          parsed = null;
        }
        if (response.ok && parsed?.code === 'OK') {
          setFormStatus('success');
          form.reset();
        } else {
          const msg =
            parsed?.meta?.message ||
            parsed?.message ||
            parsed?.meta?.detail ||
            responseText ||
            '提交失败，请稍后重试';
          throw new Error(msg);
        }
      })
      .catch((err) => {
        setFormStatus('error');
        setFormError(err.message || '提交失败，请检查网络后重试');
      });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20and%20elegant%20film%20research%20library%20with%20wooden%20bookshelves%20filled%20with%20cinema%20books%2C%20vintage%20film%20projector%20on%20display%2C%20soft%20reading%20lamp%20light%2C%20scholarly%20atmosphere%2C%20academic%20institution%20setting%2C%20warm%20amber%20tones%2C%20intellectual%20and%20inviting%20space&width=1600&height=600&seq=about-hero&orientation=landscape"
            alt="关于港片影志"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/50"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-7xl mx-auto">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-2">
                关于我们
              </h1>
              <p className="text-white/70 text-sm md:text-base max-w-2xl">
                了解港片影志的品牌故事、专业声誉与联系方式
              </p>
            </div>
          </div>
        </section>

        <section className="py-6 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>
        </section>

        <section className="py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              品牌介绍
            </h2>
            <div className="prose max-w-none">
              <p className="text-foreground-700 leading-relaxed mb-4">
                港片影志创立于2024年，是华语互联网上首个专注于香港三级片影史研究的专业文化平台。
                我们的使命是以学术严谨的态度、通俗易懂的表达，系统梳理香港电影分级制度的来龙去脉，
                深入解读经典三级片作品的艺术价值与文化意义，为华语电影爱好者和研究者提供权威可靠的参考资料。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                香港三级片作为华语电影史上一个特殊而重要的篇章，长期以来面临着被简单标签化、
                被主流话语忽视的处境。我们认为，任何电影现象都值得严肃对待和深入研究。
                三级片不仅是香港电影工业的重要组成部分，更是解读香港社会变迁、文化心理和集体记忆的独特切入点。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                港片影志的内容涵盖五大核心板块：分级制度演变追溯制度史、经典影片推荐提供深度解析、
                影人列传讲述创作者故事、文化解读从社会学视角展开分析、黄金时代回顾记录产业变迁。
                我们坚持每一篇文章都经过严谨的资料查证和多轮编辑审校，力求在专业性、可读性和客观性之间取得平衡。
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              专业声誉
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-article-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">
                  深度原创
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  每篇文章均由资深影迷和电影研究者撰写，经过多轮编辑校审，确保内容品质。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-shield-check-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">
                  严谨考证
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  所有影史事实均参考公开资料与学术文献，拒绝道听途说，力求客观准确。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-user-heart-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">
                  影迷社区
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  汇聚华语圈最资深的香港电影爱好者，打造开放、理性的影史讨论空间。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqItems.map((item, index) => (
                <details
                  key={index}
                  className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-medium text-foreground-800 text-sm">
                    {item.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 mb-8 text-center">
              有任何问题、建议或合作意向，欢迎通过以下表单联系我们
            </p>

            {formStatus === 'success' ? (
              <div className="p-8 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-check-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">
                  提交成功
                </h3>
                <p className="text-sm text-foreground-600">
                  感谢您的来信，我们会在2-3个工作日内回复您。
                </p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-4 px-5 py-2 text-sm rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送新消息
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form="true"
                onSubmit={handleSubmit}
                className="space-y-5 p-6 rounded-lg bg-background-50 border border-background-200/70"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的姓名
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    消息内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                    placeholder="请输入您的消息（限500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1">限500字符以内</p>
                </div>

                <div className="visually-hidden-field">
                  <label htmlFor="phone_alt">备用联系方式</label>
                  <input
                    type="text"
                    id="phone_alt"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {formStatus === 'error' && (
                  <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
                >
                  发送消息
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于港片影志',
            description: '了解港片影志的品牌故事、专业声誉与联系方式。香港电影影史专业研究平台。',
            url: 'https://pczqmcf.cn/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '港片影志',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.a,
              },
            })),
          }),
        }}
      />
    </>
  );
}